/**
 * 网格展开：把稀疏的 CellIR 列表补全为完整的 rowCount × columnCount 网格。
 *
 * 规则（依据真实样本与引擎语义）：
 * 1. 每个 (row,col) 必须有且仅有一个 <cell>。用户只描述有内容的格子；
 *    空白格由转换器补全为默认静态空单元格。
 * 2. 合并：主格写 rowspan/colspan；被覆盖的区域（其 (row,col) 落在某主格
 *    的合并矩形内）**不输出为独立 <cell>**（BaskReport 语义：被覆盖格不单列）。
 * 3. 坐标名 (name) 缺省时按 (row,col) 推导为 A1 风格。
 * 4. 合并区域必须完全落在网格内且不重叠，否则报错。
 */

import type { CellIR, ReportIR } from '../ir/types.js';
import { coordName } from './coordinate.js';

export interface ExpandedCell {
  /** 补全后的完整单元格（含 name、默认属性）。 */
  cell: CellIR;
  /** 是否被其它合并主格覆盖（覆盖格不输出）。 */
  covered: boolean;
}

export interface ExpandedGrid {
  cells: ExpandedCell[];
  rowCount: number;
  columnCount: number;
}

export class GridError extends Error {}

/** 计算被某主格合并覆盖的所有坐标（不含主格自身）。 */
function coveredCoords(
  row: number,
  col: number,
  rowspan: number,
  colspan: number,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let r = row; r < row + rowspan; r++) {
    for (let c = col; c < col + colspan; c++) {
      if (r === row && c === col) continue;
      out.push([r, c]);
    }
  }
  return out;
}

export function expandGrid(report: ReportIR): ExpandedGrid {
  const { rowCount, columnCount, cells } = report;

  // 索引用户提供的单元格
  const byCoord = new Map<string, CellIR>();
  for (const c of cells) {
    if (c.row < 1 || c.row > rowCount || c.col < 1 || c.col > columnCount) {
      throw new GridError(
        `单元格 (${c.row},${c.col}) 越界：网格为 ${rowCount}×${columnCount}`,
      );
    }
    const key = `${c.row},${c.col}`;
    if (byCoord.has(key)) {
      throw new GridError(`单元格 (${c.row},${c.col}) 重复定义`);
    }
    byCoord.set(key, c);
  }

  // 计算合并覆盖集合
  const covered = new Set<string>();
  for (const c of cells) {
    const rowspan = c.rowspan ?? 1;
    const colspan = c.colspan ?? 1;
    if (rowspan < 1 || colspan < 1) {
      throw new GridError(`单元格 (${c.row},${c.col}) 的 rowspan/colspan 必须 ≥1`);
    }
    if (c.row + rowspan - 1 > rowCount || c.col + colspan - 1 > columnCount) {
      throw new GridError(
        `单元格 (${c.row},${c.col}) 合并区域 (${rowspan}×${colspan}) 越界`,
      );
    }
    for (const [cr, cc] of coveredCoords(c.row, c.col, rowspan, colspan)) {
      const key = `${cr},${cc}`;
      if (byCoord.has(key)) {
        throw new GridError(
          `单元格 (${c.row},${c.col}) 的合并区域覆盖了已定义的单元格 (${cr},${cc})`,
        );
      }
      covered.add(key);
    }
  }

  const result: ExpandedCell[] = [];
  for (let r = 1; r <= rowCount; r++) {
    for (let c = 1; c <= columnCount; c++) {
      const key = `${r},${c}`;
      const isCovered = covered.has(key);
      const base = byCoord.get(key);
      const cell: CellIR = base
        ? { ...base, name: base.name ?? coordName(r, c) }
        : {
            row: r,
            col: c,
            name: coordName(r, c),
            expand: 'none',
          };
      result.push({ cell, covered: isCovered });
    }
  }

  return { cells: result, rowCount, columnCount };
}
